import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  getServices,
  createService,
  updateService,
  deleteService,
} from "../lib/api.ts";
import { CloseIcon, PlusIcon } from "../components/Icon.tsx";
import { useIsMobile } from "../lib/use-is-mobile.ts";

interface Service {
  id: string;
  name: string;
  duration_min: number;
  price_cents: number;
  description: string;
  is_active: boolean;
  sort_order: number;
  accent_hex?: string | null;
}

const emptyForm = {
  name: "",
  duration_min: 60,
  price_cents: 0,
  description: "",
  is_active: true,
  meeting_mode: "in_person",
  accent_hex: "" as string | null | undefined,
};

const ACCENT_PRESETS = [
  { label: "Default", value: "" },
  { label: "Indigo", value: "#6366f1" },
  { label: "Forest", value: "#16a34a" },
  { label: "Rose", value: "#f43f5e" },
  { label: "Amber", value: "#f59e0b" },
  { label: "Slate", value: "#64748b" },
  { label: "Sky", value: "#0ea5e9" },
  { label: "Violet", value: "#8b5cf6" },
];

type ServiceForm = typeof emptyForm;

function ServiceModal({
  initial,
  onSave,
  onClose,
}: {
  initial?: Partial<ServiceForm>;
  onSave: (data: ServiceForm) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<ServiceForm>({ ...emptyForm, ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof ServiceForm>(k: K, v: ServiceForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center"
      style={{ background: "rgba(0,0,0,0.55)" }}
    >
      <div
        className="w-full md:max-w-md p-6 md:p-7 rounded-t-2xl md:rounded-2xl max-h-[92vh] overflow-y-auto"
        style={{
          background: "var(--paper-raised)",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <div className="flex items-center justify-between mb-5">
          <h2
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontSize: 22,
              fontWeight: 500,
              letterSpacing: "-0.012em",
              color: "var(--ink)",
            }}
          >
            {initial ? "Edit service" : "New service"}
          </h2>
          <button
            onClick={onClose}
            className="zd-icon-btn"
            aria-label="Close"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {error && <div className="alert-error mb-4">{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label">Name</label>
            <input
              required
              className="input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Duration (min)</label>
              <input
                required
                type="number"
                min={5}
                className="input"
                value={form.duration_min}
                onChange={(e) => set("duration_min", Number(e.target.value))}
              />
            </div>
            <div>
              <label className="label">Price ($)</label>
              <input
                required
                type="number"
                min={0}
                step={0.01}
                className="input"
                value={(form.price_cents / 100).toFixed(2)}
                onChange={(e) => set("price_cents", Math.round(Number(e.target.value) * 100))}
              />
            </div>
          </div>
          <div>
            <label className="label">Description</label>
            <textarea
              rows={3}
              className="input resize-none"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </div>
          <div>
            <label className="label">Meeting Type</label>
            <select className="input" value={form.meeting_mode} onChange={e => set("meeting_mode", e.target.value)}>
              <option value="in_person">In-person</option>
              <option value="video">Video call</option>
              <option value="phone">Phone call</option>
            </select>
          </div>
          <div>
            <label className="label">Calendar accent</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {ACCENT_PRESETS.map((p) => {
                const active = (form.accent_hex || "") === p.value;
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => set("accent_hex", p.value)}
                    title={p.label}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      border: active ? "2px solid var(--ink)" : "1px solid var(--rule)",
                      background: p.value || "transparent",
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    {!p.value && (
                      <span style={{ fontSize: 10, color: "var(--ink-faint)" }}>—</span>
                    )}
                  </button>
                );
              })}
              <input
                type="color"
                value={form.accent_hex || "#6366f1"}
                onChange={(e) => set("accent_hex", e.target.value)}
                title="Custom hex"
                style={{ width: 32, height: 32, padding: 0, border: "1px solid var(--rule)", borderRadius: 8, cursor: "pointer", background: "transparent" }}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="is_active"
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set("is_active", e.target.checked)}
              className="h-4 w-4 rounded"
            />
            <label htmlFor="is_active" className="text-sm" style={{ color: "var(--text)" }}>Active</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Services() {
  const { slug } = useParams<{ slug: string }>();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<{ open: boolean; service?: Service }>({ open: false });
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    getServices(slug)
      .then((data: Service[]) => setServices(data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data: typeof emptyForm) => {
    if (!slug) return;
    if (modal.service) {
      const updated = await updateService(slug, modal.service.id, data) as Service;
      setServices((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } else {
      const created = await createService(slug, data) as Service;
      setServices((prev) => [...prev, created]);
    }
  };

  const handleDelete = async (id: string) => {
    if (!slug || !confirm("Delete this service?")) return;
    setDeletingId(id);
    try {
      await deleteService(slug, id);
      setServices((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (svc: Service) => {
    if (!slug) return;
    const updated = await updateService(slug, svc.id, { is_active: !svc.is_active }) as Service;
    setServices((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  const moveUp = async (idx: number) => {
    if (!slug || idx === 0) return;
    const reordered = [...services];
    [reordered[idx - 1], reordered[idx]] = [reordered[idx], reordered[idx - 1]];
    setServices(reordered);
    await updateService(slug, reordered[idx - 1].id, { sort_order: idx - 1 });
    await updateService(slug, reordered[idx].id, { sort_order: idx });
  };

  const moveDown = async (idx: number) => {
    if (!slug || idx === services.length - 1) return;
    const reordered = [...services];
    [reordered[idx], reordered[idx + 1]] = [reordered[idx + 1], reordered[idx]];
    setServices(reordered);
    await updateService(slug, reordered[idx].id, { sort_order: idx });
    await updateService(slug, reordered[idx + 1].id, { sort_order: idx + 1 });
  };

  const isMobile = useIsMobile();

  return (
    <div>
      {/* Page hero */}
      <section className="zd-hero" style={{ marginBottom: 32 }}>
        <div className="zd-hero-text">
          <div className="zd-hero-kicker">
            Catalog <span className="dot">·</span> {services.length} {services.length === 1 ? "service" : "services"}
          </div>
          <h1 className="zd-hero-headline">Services</h1>
          <p className="zd-hero-lead">What you offer, how long it takes, and what it costs. Reorder rows to control how customers see them.</p>
        </div>
        <button onClick={() => setModal({ open: true })} className="zd-hero-cta">
          <PlusIcon /> New service
        </button>
      </section>

      {error && <div className="alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ padding: "48px 0", textAlign: "center", color: "var(--ink-faint)", fontSize: 14 }}>
          Loading…
        </div>
      ) : services.length === 0 ? (
        <div className="zd-empty">
          <h3>Nothing in the catalog yet.</h3>
          <p>Add your first service so customers can book. Most owners start with one signature offering and expand from there.</p>
          <div className="cta-row">
            <button onClick={() => setModal({ open: true })} className="zd-hero-cta">
              <PlusIcon /> New service
            </button>
          </div>
        </div>
      ) : isMobile ? (
        <div className="zd-card-list">
          {services.map((svc, idx) => (
            <div key={svc.id} className="zd-card">
              <div className="zd-card-row">
                <span style={{ fontWeight: 600, fontSize: 15, color: "var(--ink)" }}>{svc.name}</span>
                <span style={{ fontWeight: 600, fontSize: 14, fontFeatureSettings: "'tnum'" }}>${(svc.price_cents / 100).toFixed(2)}</span>
              </div>
              {svc.description && (
                <div style={{ fontSize: 12.5, color: "var(--ink-soft)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{svc.description}</div>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontSize: 12.5, color: "var(--ink-soft)" }}>
                <span style={{ fontFeatureSettings: "'tnum'" }}>{svc.duration_min} min</span>
                <button onClick={() => handleToggleActive(svc)} className={svc.is_active ? "badge-green" : "badge-gray"} style={{ cursor: "pointer", border: "none" }}>
                  {svc.is_active ? "Active" : "Inactive"}
                </button>
              </div>
              <div className="zd-card-actions">
                <button onClick={() => moveUp(idx)} disabled={idx === 0} className="btn-secondary" style={{ flex: "0 0 auto" }} aria-label="Move up">↑</button>
                <button onClick={() => moveDown(idx)} disabled={idx === services.length - 1} className="btn-secondary" style={{ flex: "0 0 auto" }} aria-label="Move down">↓</button>
                <button onClick={() => setModal({ open: true, service: svc })} className="btn-secondary">Edit</button>
                <button onClick={() => handleDelete(svc.id)} disabled={deletingId === svc.id} className="btn-secondary" style={{ color: "var(--status-cancelled)" }}>
                  {deletingId === svc.id ? "…" : "Delete"}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            background: "var(--paper-raised)",
            borderRadius: 14,
            boxShadow: "var(--shadow-sm)",
            overflow: "hidden",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--paper-well)" }}>
                {["Order", "Name", "Duration", "Price", "Status", ""].map((h, i) => (
                  <th
                    key={i}
                    style={{
                      padding: "14px 18px",
                      textAlign: i === 5 ? "right" : "left",
                      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                      fontSize: 10.5,
                      fontWeight: 500,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: "var(--ink-faint)",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {services.map((svc, idx) => (
                <tr key={svc.id} style={{ borderTop: "1px solid var(--rule)" }}>
                  <td style={{ padding: "14px 18px", verticalAlign: "middle", width: 60 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <button
                        onClick={() => moveUp(idx)}
                        disabled={idx === 0}
                        aria-label="Move up"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: idx === 0 ? "default" : "pointer",
                          color: idx === 0 ? "var(--ink-ghost)" : "var(--ink-soft)",
                          padding: 2,
                          opacity: idx === 0 ? 0.3 : 1,
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6" /></svg>
                      </button>
                      <button
                        onClick={() => moveDown(idx)}
                        disabled={idx === services.length - 1}
                        aria-label="Move down"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: idx === services.length - 1 ? "default" : "pointer",
                          color: idx === services.length - 1 ? "var(--ink-ghost)" : "var(--ink-soft)",
                          padding: 2,
                          opacity: idx === services.length - 1 ? 0.3 : 1,
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: "14px 18px" }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>{svc.name}</div>
                    {svc.description && (
                      <div style={{ fontSize: 12.5, color: "var(--ink-soft)", marginTop: 2, maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {svc.description}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "14px 18px", fontSize: 14, color: "var(--ink-soft)", fontFeatureSettings: "'tnum'" }}>
                    {svc.duration_min} min
                  </td>
                  <td style={{ padding: "14px 18px", fontSize: 14, color: "var(--ink)", fontFeatureSettings: "'tnum'", fontWeight: 500 }}>
                    ${(svc.price_cents / 100).toFixed(2)}
                  </td>
                  <td style={{ padding: "14px 18px" }}>
                    <button
                      onClick={() => handleToggleActive(svc)}
                      className={svc.is_active ? "badge-green" : "badge-gray"}
                      style={{ cursor: "pointer", border: "none" }}
                      title={`Click to ${svc.is_active ? "deactivate" : "activate"}`}
                    >
                      {svc.is_active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td style={{ padding: "14px 18px", textAlign: "right" }}>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      <button
                        onClick={() => setModal({ open: true, service: svc })}
                        className="zd-ghost-btn"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(svc.id)}
                        disabled={deletingId === svc.id}
                        className="zd-ghost-btn"
                        style={{ color: "var(--status-cancelled)" }}
                      >
                        {deletingId === svc.id ? "…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal.open && (
        <ServiceModal
          initial={modal.service}
          onSave={handleSave}
          onClose={() => setModal({ open: false })}
        />
      )}
    </div>
  );
}
